import { TableCell } from "@/components/ui/table";
import { formatMonth } from "@/lib/keyword-volume-parser";
import { cn } from "@/lib/utils";
import { DERIVED_METRICS } from "./clicksGridEngine";
import type { CampaignStat } from "@/types/clicks";
import { CompareDelta } from "./CompareDelta";
import {
  formatMetricValue,
  getMetricCellTone,
  getMetricDisplay,
  getMetricNumericValue,
  getMetricValueClass,
  type MetricFieldId,
} from "./metricDisplay";

interface MetricCellProps {
  metricId: MetricFieldId;
  row: CampaignStat;
  prevRow?: CampaignStat;
  cellClass: string;
  /** Right-click, with the value as the user sees it rather than the raw number. */
  onContextMenu?: (event: React.MouseEvent, text: string) => void;
}

export function MetricCell({
  metricId,
  row,
  prevRow,
  cellClass,
  onContextMenu,
}: MetricCellProps) {
  const { format, nullable, nullTitle, lowerIsBetter } = getMetricDisplay(metricId);
  const unknown = nullable && row[metricId as keyof CampaignStat] == null;
  const value = getMetricNumericValue(row, metricId);
  // A previous row without this metric has no baseline: GSC, keyword volume,
  // capture rate and custom metrics are merged onto the current rows only, and
  // read as 0 they showed "+100%" under every such cell. Derived metrics (uEPC,
  // Approve %…) come from fields the previous row does have.
  const previous =
    prevRow && (metricId in DERIVED_METRICS || prevRow[metricId as keyof CampaignStat] != null)
      ? getMetricNumericValue(prevRow, metricId)
      : undefined;

  return (
    <TableCell
      className={cn(
        cellClass,
        "text-right align-top",
        getMetricCellTone(metricId),
      )}
      onContextMenu={
        onContextMenu
          ? (event) => onContextMenu(event, unknown ? "—" : formatMetricValue(value, format))
          : undefined
      }
    >
      {unknown ? (
        <div
          className="text-muted-foreground/70"
          title={nullTitle ?? "Not classified yet: the period includes days computed before our bot verdict"}
        >
          —
        </div>
      ) : (
        <>
          <div className={getMetricValueClass(metricId, value)}>
            {formatMetricValue(value, format)}
            {/* Rows reach different latest months (each keywordtool file has its
                own M1), so the month sits next to the number, not only in a tooltip. */}
            {metricId === "kv_last" && row.kv_last_month && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                · {formatMonth(row.kv_last_month, "en-US")}
              </span>
            )}
          </div>
          <CompareDelta current={value} previous={previous} invertPolarity={lowerIsBetter} />
        </>
      )}
    </TableCell>
  );
}
